import { computed } from 'vue';
import { useMounted } from '@vueuse/core';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { formatDateTime } from '@/helpers/date-helper';
import { useExperiment } from '@/libraries/experiment';
import { getExperimentAffiliateTag } from '@/libraries/experiment/experiment-affiliation';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';

const ELIGIBLE_PROVIDERS = ['atp', 'amp'];
const US_ONLY_PROVIDERS = ['hlu'];

export function useLinearTVExp() {
	const { country } = storeToRefs(useLanguageStore());
	const { activeVariantWithControlGroup: variant, experimentInfo } = useExperiment('EXP_LINEAR_TV_2');

	function setLinearTVExpTags({
		url,
		type,
		shortName,
		technicalName,
	}: {
		url: ClickoutUrl;
		type: 'ro' | 'ft';
		shortName: string;
		technicalName?: string;
	}) {
		if (!variant.value) return url;

		const isEligible =
			ELIGIBLE_PROVIDERS.includes(shortName) ||
			(US_ONLY_PROVIDERS.includes(shortName) && country.value === 'US') ||
			// US is switching to Amazon Starz Ch FT for a few days.
			(shortName === 'saz' && type === 'ft');
		if (!isEligible) return url;

		const affiliationShortId = experimentInfo.value.affiliationShortId as string;

		const tag = getExperimentAffiliateTag({
			affiliationShortId,
			variant: variant.value,
			country: country.value,
			placement: type,
			providerShortName: shortName,
			technicalName,
		});

		return url.set('uct_ctag', tag);
	}

	return {
		setLinearTVExpTags,
	};
}

function compareByAvailableFromTime(aTime: any, bTime: any): number {
	return new Date(aTime ?? 0).getTime() - new Date(bTime ?? 0).getTime();
}

/** Sorts offers chronologically by `availableFromTime`. */
export function sortOffersByAvailableFromTime<T extends { availableFromTime?: any }>(offers: T[]): T[] {
	return [...offers].sort((a, b) => compareByAvailableFromTime(a.availableFromTime, b.availableFromTime));
}

/**
 * Sorts offer episodes chronologically by `availableFromTime` and removes
 * duplicates that share the same `availableFromTime` and `episode.id`.
 */
export function sortAndDedupeOfferEpisodes<T extends { offer: { availableFromTime?: any }; episode: { id: string } }>(
	offerEpisodes: T[]
): T[] {
	const seen = new Set<string>();

	return [...offerEpisodes]
		.sort((a, b) => compareByAvailableFromTime(a.offer.availableFromTime, b.offer.availableFromTime))
		.filter(({ offer, episode }) => {
			const key = `${episode.id}-${offer.availableFromTime}`;
			if (seen.has(key)) return false;

			seen.add(key);
			return true;
		});
}

const ESTTimeZone = 'America/New_York';
const DETimeZone = 'Europe/Berlin';

/**
 * Consistent broadcast-time formatting for linear/FAST TV offers. The displayed
 * date and time are always resolved to the same timezone (the channel's
 * broadcast timezone), so a date label can never disagree with the time label
 * it's paired with.
 */
export function useLinearBroadcastTime() {
	const { country, language } = storeToRefs(useLanguageStore());
	const isMounted = useMounted();

	const broadcastTimeZone = computed(() => {
		if (country.value === 'US') return ESTTimeZone;
		if (country.value === 'DE') return DETimeZone;
		return isMounted.value ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC';
	});

	function v2Date(availableFromTime: string | null | undefined) {
		if (!availableFromTime) {
			return null;
		}

		return formatDateTime(new Date(availableFromTime), language.value, country.value, {
			weekday: 'short',
			day: '2-digit',
			month: '2-digit',
			timeZone: broadcastTimeZone.value,
		});
	}

	function v2Time(availableFromTime: string | null | undefined, availableToTime: string | null | undefined) {
		if (!availableFromTime) {
			return null;
		}

		const rawDateStart = new Date(availableFromTime as unknown as Date);
		const rawDateEnd = new Date(availableToTime as unknown as Date);
		const timeZone = broadcastTimeZone.value;

		const start = formatDateTime(rawDateStart, language.value, country.value, {
			hour: '2-digit',
			minute: '2-digit',
			timeZone,
			hourCycle: 'h23',
		});

		const end = formatDateTime(rawDateEnd, language.value, country.value, {
			hour: '2-digit',
			minute: '2-digit',
			timeZone,
			timeZoneName: 'short',
			hourCycle: 'h23',
		});

		return `${start} - ${end}`;
	}

	return { broadcastTimeZone, v2Date, v2Time };
}
